import { Camera } from 'expo-camera'
import { useCallback } from 'react'
import { Linking, Platform } from 'react-native'

import { useStore } from '../../../models'
import { useAVPermissions } from '../../../components/ANewDesign/ModalContent/Content/AVPermissions/AVPermissionsModal'

export const hasCameraPermission = async () => {
  const permissionCamera = await Camera.getCameraPermissionsAsync()

  return permissionCamera?.granted ?? false
}

export const hasMicrophonePermission = async () => {
  const permissionCamera = await Camera.getMicrophonePermissionsAsync()

  return permissionCamera?.granted ?? false
}

export const requestCameraPermission = async () => {
  return (await Camera.requestCameraPermissionsAsync()).granted
}

export const requestMicrophonePermission = async () => {
  return (await Camera.requestMicrophonePermissionsAsync()).granted
}

export const hasAVPermissions = async () => {
  const hasCamPermission = await hasCameraPermission()
  const hasMicPermission = await hasMicrophonePermission()

  return hasCamPermission && hasMicPermission
}

//TODO: Remove unneded code from native platforms on web

export const useJoinCall = ({ onClose }: { onClose?: () => void }) => {
  const {
    game: { seatedPlayersWithSeatedObserver: players, observers, setPreviewVideoWasShown, setIsCallOptionsModalOpen },
    session,
    communication: { startCall }
  } = useStore()
  const { setPermissionConfig } = useAVPermissions()

  const requestCameraAndAudioPermission = useCallback(async () => {
    return false
  }, [])

  const checkAndRequestForCameraPermission = async () => {
    if (Platform.OS === 'ios') {
      const hasPermissionCamera = await requestCameraPermission()

      return hasPermissionCamera
    }

    return false
  }

  const checkAndRequestForMicPermission = async () => {
    if (Platform.OS === 'ios') {
      const hasPermissionMic = await requestMicrophonePermission()

      return hasPermissionMic
    }

    return false
  }

  const handleAudioVideoPermissions = useCallback(async () => {
    if (Platform.OS === 'ios') {
      const hasPermissionCamera = await requestCameraPermission()
      const hasPermissionMic = await requestMicrophonePermission()

      if (hasPermissionCamera && hasPermissionMic) {
        return true
      }
    } else if (Platform.OS === 'android') {
      const androidPermissionsGranted = await requestCameraAndAudioPermission()
      if (!androidPermissionsGranted) {
        // game.pushGameModals(
        //   GameModal.create({
        //     kind: GameModalKinds.options,
        //     props: {},
        //     layerType: LayerType.alert // noop
        //     //timeToKeepOpen: 5 // TODO: Should we keep this bigger than sleep? Or exactly?
        //   })
        // )
      }
      return androidPermissionsGranted
    }

    return false
  }, [requestCameraAndAudioPermission])

  const goToSettings = () => {
    if (Platform.OS === 'ios') {
      Linking.openURL('app-settings:')
    } else {
      Linking.openSettings()
    }
  }

  const onJoinVideo = async () => {
    let hasMicPermissionOn = await hasMicrophonePermission()
    let hasCamPermissionOn = await hasCameraPermission()

    let allowPrompted = false

    if (!(hasMicPermissionOn && hasCamPermissionOn)) {
      setPermissionConfig({
        isAVPermissionsOpen: true,
        isAllowPrompted: true
      })
      allowPrompted = true
    }

    hasMicPermissionOn = await requestMicrophonePermission()
    if (!hasMicPermissionOn) {
      setPermissionConfig({
        isAVPermissionsOpen: true,
        isAllowPrompted: false,
        blockedPermission: 'microphone'
      })
      return
    }

    hasCamPermissionOn = await requestCameraPermission()
    if (!hasCamPermissionOn) {
      setPermissionConfig({
        isAVPermissionsOpen: true,
        isAllowPrompted: false,
        blockedPermission: 'camera'
      })
      return
    }

    if (allowPrompted) {
      setPermissionConfig({
        isAVPermissionsOpen: false
      })
    }

    onClose?.()
    console.debug('onJoinVideo')
    try {
      setPreviewVideoWasShown(false)
      const playerUserId = session.user?._id
      const myPlayerData = players.filter((data) => data.userId === playerUserId)
      if (myPlayerData.length > 0) {
        const playerId = Number(myPlayerData[0].id)
        await startCall({
          uid: playerId,
          withCamera: true,
          muted: false,
          hasMicPermission: true,
          hasCamPermission: true
        })
        //setUserTappedOnGoLive(true)
        //setPreviewVideoWasShown(false)
      }

      console.debug('onJoinVideo with Video api requested')
      setIsCallOptionsModalOpen(false)
    } catch (error) {
      console.error('onJoinVideo or Agora not available', error)
    } finally {
      console.debug('onJoinVideo finally store cleared')
    }
  }

  const onJoinAudio = async () => {
    let hasMicPermissionOn = await hasMicrophonePermission()

    let allowPrompted = false

    if (!hasMicPermissionOn) {
      setPermissionConfig({
        isAVPermissionsOpen: true,
        isAllowPrompted: true
      })
      allowPrompted = true
    }

    hasMicPermissionOn = await requestMicrophonePermission()

    if (!hasMicPermissionOn) {
      setPermissionConfig({
        isAVPermissionsOpen: true,
        isAllowPrompted: false,
        blockedPermission: 'microphone'
      })
      return
    }

    if (allowPrompted) {
      setPermissionConfig({
        isAVPermissionsOpen: false
      })
    }

    onClose?.()
    const playerUserId = session.user?._id
    const myPlayerData = players.filter((data) => data.userId === playerUserId)
    if (myPlayerData.length > 0) {
      const playerId = Number(myPlayerData[0].id)
      await startCall({
        uid: playerId,
        withCamera: false,
        muted: false,
        hasMicPermission: true,
        hasCamPermission: false
      })
      setPreviewVideoWasShown(true)
    }
    setIsCallOptionsModalOpen(false)
  }

  const onJoinWithAvOff = async () => {
    const hasCamPermission = await hasCameraPermission()
    const hasMicPermission = await hasMicrophonePermission()
    onClose?.()
    const playerUserId = session.user?._id
    if (!playerUserId) return

    const myPlayerData = players.filter((data) => data.userId === playerUserId)
    if (myPlayerData.length > 0) {
      const playerId = Number(myPlayerData[0].id)
      await startCall({
        uid: playerId,
        withCamera: false,
        muted: true,
        hasCamPermission,
        hasMicPermission
      })
    } else {
      const myObserverData = observers.find((data) => data.userId === playerUserId)
      if (myObserverData) {
        await startCall({
          uid: Number(myObserverData.id),
          withCamera: false,
          muted: true,
          hasCamPermission,
          hasMicPermission
        })
      }
    }
    setPreviewVideoWasShown(true)
    setIsCallOptionsModalOpen(false)
  }

  return {
    onJoinAudio,
    onJoinVideo,
    onJoinWithAvOff,
    handleAudioVideoPermissions,
    checkAndRequestForCameraPermission,
    checkAndRequestForMicPermission
  }
}
